"use client";

import { useState, ChangeEvent, useMemo, Suspense } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { supabase } from "@/lib/supabase";
import LightRays from "@/components/LightRays";
import Image from "next/image";
import { Lock, Mail, Loader2, ArrowRight, AlertTriangle } from "lucide-react";
import posthog from "posthog-js";

// ═══════════════════════════════════════════════════════════════════════════
// ROLE-BASED REDIRECT MAPPING
// ═══════════════════════════════════════════════════════════════════════════
type ProfileRole =
  | "god_admin"
  | "society_admin"
  | "manager" // Added Manager
  | "chairman" // Added Chairman
  | "secretary" // Added Secretary
  | "treasurer" // Added Treasurer
  | "staff"
  | "guard"
  | "resident";

function getRoleBasedRedirect(role: ProfileRole): string {
  switch (role) {
    case "god_admin":
      return "/god";

    // All Admin Roles go to Dashboard
    case "society_admin":
    case "manager":
    case "chairman":
    case "secretary":
    case "treasurer":
      return "/dashboard";

    case "staff":
    case "guard":
      return "/entry";

    case "resident":
    default:
      return ""; // Will be blocked
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// LOADING FALLBACK COMPONENT
// ═══════════════════════════════════════════════════════════════════════════
function LoginLoadingFallback() {
  return (
    <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <div className="w-16 h-16 bg-linear-to-tr from-orange-500 to-red-600 rounded-2xl flex items-center justify-center shadow-[0_0_40px_rgba(249,115,22,0.4)] border border-white/10">
          <Loader2 className="text-white w-8 h-8 animate-spin" />
        </div>
        <p className="text-gray-500 font-medium">Loading...</p>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// LOGIN CONTENT COMPONENT
// ═══════════════════════════════════════════════════════════════════════════
function LoginContent() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const router = useRouter();
  const searchParams = useSearchParams();

  const urlError = useMemo(() => {
    const errorParam = searchParams.get("error");
    if (errorParam === "use_mobile_app") {
      return "Residents should use the SmartDwara mobile app.";
    }
    if (errorParam === "no_profile") {
      return "No user profile found. Please contact support.";
    }
    return null;
  }, [searchParams]);

  const displayError = loginError || urlError;

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setLoginError(null);

    try {
      // STEP 1: AUTHENTICATE
      const { data: authData, error: authError } =
        await supabase.auth.signInWithPassword({
          email,
          password,
        });

      if (authError) {
        posthog.capture("login_failed", {
          error_message: authError.message,
          email: email,
        });
        setLoginError(authError.message);
        setLoading(false);
        return;
      }

      if (!authData.user) {
        setLoginError("Authentication failed. Please try again.");
        setLoading(false);
        return;
      }

      // STEP 2: FETCH PROFILE (DEBUG MODE)
      console.log("🔍 Fetching profile for:", authData.user.id);

      const { data: profile, error: profileError } = await supabase
        .from("profiles")
        .select("role")
        .eq("id", authData.user.id)
        .single();

      // DEBUGGING: Show exact error if fetch fails
      if (profileError || !profile) {
        console.error("[Login] Profile fetch failed:", profileError);
        setLoginError(
          `DEBUG ERROR: ${profileError?.message || "Profile is null"} \n(Looking for ID: ${authData.user.id})`,
        );
        await supabase.auth.signOut();
        setLoading(false);
        return;
      }

      // ✅ FIX: Define userRole here so subsequent steps can use it
      const userRole = profile.role as ProfileRole;

      // STEP 3: BLOCK RESIDENTS
      if (userRole === "resident") {
        posthog.capture("resident_blocked", {
          email: email,
          user_id: authData.user.id,
          role: userRole,
        });
        await supabase.auth.signOut();
        setLoginError("Residents must use the Mobile App.");
        setLoading(false);
        return;
      }

      // ═══════════════════════════════════════════════════════════════════════
      // STEP 3.5: CHECK IF PASSWORD CHANGE REQUIRED (First Login)
      // ═══════════════════════════════════════════════════════════════════════
      const userMetadata = authData.user.user_metadata;
      if (userMetadata?.must_change_password === true) {
        console.log(
          "[Login] First login detected - redirecting to password change",
        );
        posthog.capture("first_login_password_change", {
          email: email,
          user_id: authData.user.id,
          role: userRole,
        });
        router.push("/change-password");
        return;
      }

      // ═══════════════════════════════════════════════════════════════════════
      // STEP 3.6: GENERATE UNIQUE SESSION TOKEN (The "Stamp")
      // ═══════════════════════════════════════════════════════════════════════
      // Only for GOD ADMIN (as requested), or remove the 'if' to apply to everyone.
      if (userRole === "god_admin") {
        const newSessionToken = crypto.randomUUID();

        // A. Save to Database
        await supabase
          .from("profiles")
          .update({ session_token: newSessionToken })
          .eq("id", authData.user.id);

        // B. Save to Local Browser
        localStorage.setItem("smartdwara_session_token", newSessionToken);
      }

      // STEP 4: REDIRECT
      const redirectUrl = getRoleBasedRedirect(userRole);

      if (!redirectUrl) {
        console.warn("[Login] Unknown role:", userRole);
        await supabase.auth.signOut();
        setLoginError("Invalid user role. Please contact support.");
        setLoading(false);
        return;
      }

      console.log(`[Login] Success: ${email} (${userRole}) → ${redirectUrl}`);

      posthog.identify(authData.user.id, {
        email: email,
        role: userRole,
      });

      router.push(redirectUrl);
    } catch (err) {
      console.error("[Login] Unexpected error:", err);
      posthog.captureException(err);
      setLoginError("An unexpected error occurred.");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center relative overflow-hidden selection:bg-orange-500/30">
      <LightRays
        raysOrigin="top-center"
        raysColor="#F97316"
        raysSpeed={0.5}
        className="absolute inset-0 pointer-events-none opacity-40"
      />

      <div className="w-full max-w-md p-8 relative z-10">
        <div className="text-center mb-10">
          <Image
            src="/brand/logo.svg"
            alt="SmartDwara Logo"
            width={32}
            height={32}
            className="w-16 h-16 rounded-2xl mx-auto flex items-center justify-center mb-6 shadow-[0_0_40px_rgba(249,115,22,0.4)] "
          />
          <h1 className="text-4xl font-black tracking-tighter mb-2">
            Welcome Back
          </h1>
          <p className="text-gray-500 font-medium">SmartDwara Admin Portal</p>
        </div>

        {displayError && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-xl flex items-start gap-3">
            <AlertTriangle className="text-red-500 shrink-0 mt-0.5" size={18} />
            <p className="text-red-400 text-sm font-medium whitespace-pre-wrap">
              {displayError}
            </p>
          </div>
        )}

        <form onSubmit={handleLogin} className="space-y-6">
          <div className="space-y-2">
            <label className="text-xs font-bold text-gray-500 uppercase tracking-widest ml-1">
              Email Address
            </label>
            <div className="relative group">
              <div className="absolute left-4 top-3.5 text-gray-500 group-focus-within:text-orange-500 transition-colors">
                <Mail size={18} />
              </div>
              <input
                type="email"
                required
                value={email}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setEmail(e.target.value)
                }
                className="w-full bg-[#0A0A0A] border border-white/10 rounded-xl py-3 pl-12 pr-4 text-sm font-bold placeholder:text-gray-700 focus:outline-none focus:border-orange-500/50 focus:ring-1 focus:ring-orange-500/50 transition-all shadow-inner"
                placeholder="admin@smartdwara.com"
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-xs font-bold text-gray-500 uppercase tracking-widest ml-1">
              Password
            </label>
            <div className="relative group">
              <div className="absolute left-4 top-3.5 text-gray-500 group-focus-within:text-orange-500 transition-colors">
                <Lock size={18} />
              </div>
              <input
                type="password"
                required
                value={password}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setPassword(e.target.value)
                }
                className="w-full bg-[#0A0A0A] border border-white/10 rounded-xl py-3 pl-12 pr-4 text-sm font-bold placeholder:text-gray-700 focus:outline-none focus:border-orange-500/50 focus:ring-1 focus:ring-orange-500/50 transition-all shadow-inner"
                placeholder="••••••••"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-orange-500 text-black font-black py-4 rounded-xl hover:bg-gray-200 transition-all active:scale-95 flex items-center justify-center gap-2 group shadow-[0_0_20px_rgba(255,255,255,0.1)]"
          >
            {loading ? (
              <Loader2 className="animate-spin" />
            ) : (
              <>
                Sign In{" "}
                <ArrowRight
                  size={18}
                  className="group-hover:translate-x-1 transition-transform"
                />
              </>
            )}
          </button>

          {/* FORGOT PASSWORD LINK */}
          <div className="text-center pt-2">
            <button
              type="button"
              onClick={() => {
                if (!email) {
                  setLoginError("Please enter your email first.");
                  return;
                }
                // Request magic link
                fetch("/api/send-magic-link", {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ email, type: "recovery" }),
                })
                  .then(() => {
                    setLoginError(null);
                    alert(
                      "If this email exists, a password reset link has been sent.",
                    );
                  })
                  .catch(() => {
                    setLoginError(
                      "Failed to send reset link. Please try again.",
                    );
                  });
              }}
              className="text-sm text-gray-500 hover:text-orange-500 transition-colors"
            >
              Forgot password? Get a magic link →
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function LoginPage() {
  return (
    <Suspense fallback={<LoginLoadingFallback />}>
      <LoginContent />
    </Suspense>
  );
}
